import React from 'react'
import { ProgressBar } from '@/components/atoms/ProgressBar/ProgressBar'
import { Button } from '@/components/atoms'
import type { GradingResultSummary } from '@/components/organisms/Card/Homework/HomeworkScore'

interface HomeworkInfoProps {
  type?: 'score' | 'grading'
  platform?: 'academy' | 'admin'
  data?: any
  gradingResult?: GradingResultSummary | null
  onBulkAiGrading?: () => void
  bulkAiLoading?: boolean
  onIncompleteClick?: () => void
}

interface Homework {
  status: '채점중' | '채점대기' | '채점완료'
}

const formatDateLabel = (value?: string | null) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  const pad = (num: number) => num.toString().padStart(2, '0');
  return `${date.getFullYear()}.${pad(date.getMonth() + 1)}.${pad(date.getDate())}`;
};

function statusColor(status: Homework['status']) {  
  switch (status) {
    case '채점중':   return 'text-orange-500'
    case '채점대기': return 'text-blue-500'
    case '채점완료': return 'text-green-500'
  }
}

const HomeworkInfo = ({ type = 'score', platform = 'academy', data, gradingResult, onBulkAiGrading, bulkAiLoading = false, onIncompleteClick }: HomeworkInfoProps) => {
  const planValue = (() => {
    if (data?.academyPlanType) return String(data.academyPlanType).toLowerCase();
    if (platform === 'academy') return 'standard';
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('academy_current_plan');
      return stored ? stored.toLowerCase() : '';
    }
    return '';
  })();
  const isPlusPlan = platform === 'admin' ? true : planValue === 'plus';
  // 더미 데이터 (data가 없을 때)
  if (!data) {
    const homeworkStatus: Homework['status'] = '채점중';
    return (
      <section>
          <div className='flex gap-2 items-center mb-4'>
              <h1 className='text-lg font-bold'>숙제 이름 입니다.</h1>
              <p className={statusColor(homeworkStatus)}>{homeworkStatus}</p>
            {type === 'grading' && isPlusPlan && (
                  <Button size='small' className='ml-4' onClick={onBulkAiGrading} disabled={bulkAiLoading}>
                    {bulkAiLoading ? '채점중...' : 'AI 일괄 채점 진행하기'}
                  </Button>
              )}
          </div>
          <ProgressBar progress={50} showTitle='숙제 상태' showLabel={true} />
          <button
            type='button'
            className='p-2 px-3 mt-2 w-full text-left text-sm text-red-500 rounded-xl border border-red-200'
            onClick={onIncompleteClick}
          >
            아직 진행하지 않은 숙제 문항이 있어요!
          </button>
          <p className='my-4 text-sm text-gray-500'>숙제기간: 2025.08.01 ~ 2025.08.07</p>
          <div className='px-4 py-3 bg-gray-50 rounded-2xl'>
              <span className='text-xs font-bold text-gray-500'>숙제 범위</span>
              <p>Jungle Phonics (A unit)</p>
          </div>
      </section>
    );
  }

  // 실제 데이터
  const homeworkStatus: Homework['status'] = data.status || '채점대기';
  const progress = (data.progress ?? data.completionRate ?? data.completionPercent ?? data.completionPercentage ?? 0) as number;
  const startDate = data.startDate || '';
  const endDate = data.endDate || '';
  const textbooks = data.textbooks || [];
  const homeworkTitle = data.scheduleTitle || data.homeworkTitle || '숙제 이름';
  const gradedDate = data?.gradingCompletedAt || data?.gradedAt || data?.completedAt || data?.updatedAt || '';
  const gradedDateLabel = gradedDate ? formatDateLabel(gradedDate) : '';

  // 채점 통계 - gradingResult prop이 있으면 사용, 없으면 기본값
  const summaryReady = !!gradingResult;
  const totalItems = gradingResult?.totalItems ?? 0;
  const correctItems = gradingResult?.correct ?? 0;
  const incorrectItems = gradingResult?.incorrect ?? 0;
  const gradingItems = gradingResult?.grading ?? 0;

  return (
    <section>
        <div className='flex gap-2 items-center mb-4'>
            <h1 className='text-lg font-bold'>{homeworkTitle}</h1>
            <p className={statusColor(homeworkStatus)}>{homeworkStatus}</p>
            {type === 'grading' && isPlusPlan && (
                <Button size='small' className='ml-4' onClick={onBulkAiGrading} disabled={bulkAiLoading}>
                  {bulkAiLoading ? '채점중...' : 'AI 일괄 채점 진행하기'}
                </Button>
            )}
        </div>
        <ProgressBar progress={progress} showTitle='숙제 상태' showLabel={true} />
        {/* 학생단에 만 노출 */}
        {type === 'score' && progress < 100 && (
          <button
            type='button'
            className='p-2 px-3 mt-2 w-full text-left text-sm text-red-500 rounded-xl border border-red-200'
            onClick={onIncompleteClick}
          >
            아직 진행하지 않은 숙제 문항이 있어요!
          </button>
        )}
        <p className='my-4 text-sm text-gray-500'>숙제기간: {startDate} ~ {endDate}</p>
        <dl className='grid gap-2 p-4 mb-4 text-sm bg-gray-50 rounded-2xl'>
          <div className='flex justify-between'>
            <dt className='font-semibold text-gray-600'>숙제명</dt>
            <dd className='text-gray-900'>{homeworkTitle}</dd>
          </div>
          <div className='flex justify-between'>
            <dt className='font-semibold text-gray-600'>숙제기간</dt>
            <dd className='text-gray-900'>{startDate && endDate ? `${startDate} ~ ${endDate}` : '-'}</dd>
          </div>
          <div className='flex justify-between'>
            <dt className='font-semibold text-gray-600'>채점일</dt>
            <dd className='text-gray-900'>{gradedDateLabel || '-'}</dd>
          </div>
        </dl>
        <div className='px-4 py-3 bg-gray-50 rounded-2xl'>
            <span className='text-xs font-bold text-gray-500'>숙제 범위</span>
            {textbooks.map((textbook: any, idx: number) => (
              <p 
                key={idx} 
                className='cursor-pointer hover:text-blue-600 transition-colors'
                onClick={() => {
                  const targetElement = document.getElementById(`textbook-${idx}`);
                  if (targetElement) {
                    targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }}
              >
                {textbook.textbookTitle} ({textbook.units.length} units)
              </p>
            ))}
        </div>
        {/* 채점 결과 */}
        <div className='px-4 py-3 bg-gray-50 rounded-2xl mt-2'>
        <p className='mb-1 text-xs font-bold text-gray-500'>채점 결과</p>
        <ul className='flex items-center'>
         <li className='flex flex-col flex-1 justify-center items-center'>
            <span className='text-2xl font-bold'>
              {summaryReady ? totalItems : '–'}
            </span>
            <p>총 문항</p>
        </li>

        <li className='w-px h-5 bg-gray-300'></li>

        <li className='flex flex-col flex-1 justify-center items-center'>
            <span className='text-2xl font-bold text-green-500'>
              {summaryReady ? correctItems : '–'}
            </span>
            <p>정답 수</p>
        </li>

        <li className='w-px h-5 bg-gray-300'></li>

        <li className='flex flex-col flex-1 justify-center items-center'>
            <span className='text-2xl font-bold text-red-500'>
              {summaryReady ? incorrectItems : '–'}
            </span>
            <p>오답 수</p>
        </li>

        <li className='w-px h-5 bg-gray-300'></li>

        <li className='flex flex-col flex-1 justify-center items-center'>
            <span className='text-2xl font-bold text-orange-500'>
              {summaryReady ? gradingItems : '–'}
            </span>
            <p>채점중</p>
        </li>
        </ul>
        </div>
    </section>
  )
}

export default HomeworkInfo 